import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';

export interface DropRow {
  id: number;
  name: string;
  students: number;
  /** Students in this class who have done the work — the only ones with a grade to lose. */
  scored_students: number;
  /** How many of those a duplicate assignment has already delivered for. */
  covered_students: number;
  /** Scored but covered by nothing. Any of these and the change is refused. */
  stranded_students: number;
  /** Whether this class on its own can come off without costing anyone a grade. */
  removable: boolean;
}

/** A class whose students are in a linked course — shown in the picker, never selectable. */
export interface KeepRow {
  course_title: string | null;
  id: number;
  name: string;
  students: number;
}

export interface AssigneePreview {
  classrooms?: DropRow[];
  keeping?: KeepRow[];
  removable?: boolean;
  blocked_reason?: string | null;
  error?: string;
}

const useAssignees = (assignmentId: string) => {
  const queryClient = useQueryClient();

  const preview = useQuery({
    enabled: assignmentId.trim().length > 0,
    queryFn: async (): Promise<AssigneePreview> => {
      const { data } = await axios.get(
        Routes.plato_api_sysadmin_grade_sync_assignees_path(assignmentId),
      );

      return data.data;
    },
    queryKey: ['gradeSyncAssignees', assignmentId],
  });

  const unassign = useMutation({
    mutationFn: async (classroomIds: number[]) => axios.post(
      Routes.plato_api_sysadmin_grade_sync_update_assignees_path(assignmentId),
      { classroom_ids: classroomIds, confirmed: true },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignees', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncRepairs'] });
    },
  });

  return { preview, unassign };
};

export default useAssignees;
